import * as React from "react";
import PropTypes from "prop-types";

import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import { Alert, Grid, Icon, Snackbar } from "@mui/material";
import TableRow from "@mui/material/TableRow";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { useEffect, useRef, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { getUserData, getAuthorizationHeader } from "Utils/helpers";
import { postData } from "Utils/server";
import "assets/theme/base/animation.css";

// Soft UI Dashboard PRO React context
import { useSoftUIController } from "context";

const oddsStylesList = {
  fractional: "FR",
  moneyline: "US",
  hongKong: "HK",
  malay: "MA",
  indonesian: "IN",
  decimal: "",
};
const MIN_ODD_COLUMN_WIDTH = 50;
const HORIZONTAL_PROVIDER_LABEL_WIDTH = 110;
const HORIZONTAL_LAYOUT_SAFE_PADDING = 8;
const HORIZONTAL_ODDS_PER_LINE_FALLBACK = {
  1: 8,
  2: 6,
  3: 4,
  4: 3,
};
//let cachedOdds = [];
function MarketView({
  market,
  massClose,
  oddsStyle,
  informParent,
  releaseOperBlocked,
  oddsProvidersLayout,
  marketsPerRow,
  showOddColumn,
  showAverageColumn,
  showTopColumn,
}) {
  const userData = getUserData();
  let adminRole = userData && userData.roles.find((item) => item.id < 50);
  const cachedPlatform = userData.defaultPlatform.value;

  const [controller, dispatch] = useSoftUIController();
  const { providers } = controller;

  const [marketData, setMarketData] = useState(null);
  const [open, setOpen] = useState(true);
  const [cachedOdds, setCachedOdds] = useState([]);
  const [notification, setNotification] = useState(false);
  const [apiResponse, setApiresponse] = useState("success");
  const [apiResponseMsg, setApiResponseMsg] = useState("success");
  const [operatorBlockedOdds, setOperatorBlockedOdds] = useState([]);
  const [operatorBlockedMarkets, setOperatorBlockedMarkets] = useState([]);
  const oddsGridHolderRef = useRef(null);
  const [oddsGridWidth, setOddsGridWidth] = useState(0);

  function isCurrentMarketFiltered() {
    if (!marketData) return false;

    const changedMarket = operatorBlockedMarkets.find((blockedMarket) => {
      return blockedMarket.uuid == marketData.marketId;
    });

    if (changedMarket) return Boolean(changedMarket.status);

    return Boolean(marketData.status == "filtered");
  }

  function showOddWithStyle(odd, forceBaseOddValue = false) {
    if (!odd) return "";

    const selectedOdd = oddsStylesList[oddsStyle];
    const oddInSelectedStyle = odd[`odd${selectedOdd}`];
    const decimalOdd = odd.odd;

    const hasSelectedStyleValue =
      oddInSelectedStyle !== undefined && oddInSelectedStyle !== null && oddInSelectedStyle !== "";
    const hasDecimalValue = decimalOdd !== undefined && decimalOdd !== null && decimalOdd !== "";

    if (forceBaseOddValue) {
      if (!hasDecimalValue) return "";

      const parsedBaseDecimal = Number(decimalOdd);
      return Number.isNaN(parsedBaseDecimal)
        ? `${decimalOdd}`
        : (Math.round(parsedBaseDecimal * 100) / 100).toFixed(2);
    }

    if (!selectedOdd || (selectedOdd == "" && oddsStyle == "decimal")) {
      if (!hasDecimalValue) return "";

      const parsedDecimal = Number(decimalOdd);
      return Number.isNaN(parsedDecimal)
        ? `${decimalOdd}`
        : (Math.round(parsedDecimal * 100) / 100).toFixed(2);
    }

    if (hasSelectedStyleValue) return oddInSelectedStyle;
    if (!hasDecimalValue) return "";

    const parsedFallbackDecimal = Number(decimalOdd);
    return Number.isNaN(parsedFallbackDecimal)
      ? `${decimalOdd}`
      : (Math.round(parsedFallbackDecimal * 100) / 100).toFixed(2);
  }

  function clickHandler(e, market) {
    e.preventDefault();
    //console.log("m_name clicked: ", m_name);
    if (e.target.name == "market-filter-switch") {
      setMarketFilter(e, market.id);
      return;
    }
    setOpen(!open);
  }

  function getNormalizedOddPrices(odd) {
    if (!odd || !odd.prices) return [];

    const oddPrices = Array.isArray(odd.prices) ? odd.prices : Object.values(odd.prices);

    return oddPrices
      .filter((price) => {
        return price && price.providerId !== undefined && price.providerId !== null;
      })
      .sort((a, b) => {
        const providerA = Number(a.providerId);
        const providerB = Number(b.providerId);

        if (Number.isNaN(providerA) || Number.isNaN(providerB)) {
          return `${a.providerId}`.localeCompare(`${b.providerId}`);
        }

        return providerA - providerB;
      });
  }

  function getProviderMeta(provider) {
    return providers.find((p) => `${p.value}` == `${provider}` || `${p.label}` == `${provider}`);
  }

  function getProviderValue(provider) {
    const providerMeta = getProviderMeta(provider);
    return providerMeta ? `${providerMeta.value}` : `${provider}`;
  }
  function isDeletedStatus(status) {
    if (status === undefined || status === null) return false;

    return `${status}`.toLowerCase().replace(/[^a-z]/g, "") == "deleted";
  }

  function getNormalizedPriceStatus(priceStatus, fallbackStatus = "new") {
    if (priceStatus === undefined || priceStatus === null || priceStatus === "") {
      return fallbackStatus;
    }

    const normalizedStatus = `${priceStatus}`.toLowerCase().replace(/[^a-z]/g, "");

    if (
      ["suspended", "suspend", "s", "blocked", "inactive", "off", "closed"].includes(
        normalizedStatus
      )
    ) {
      return "suspended";
    }

    if (["deleted", "delete", "del", "d"].includes(normalizedStatus)) {
      return "deleted";
    }

    if (["new", "nu", "n", "active", "open", "on", "ok"].includes(normalizedStatus)) {
      return "new";
    }

    return normalizedStatus;
  }

  function createProviderOdd(baseOdd, providerPrice) {
    const providerOdd = { ...baseOdd };

    if (providerPrice) {
      const normalizedProviderStatus = getNormalizedPriceStatus(
        providerPrice.status,
        baseOdd.status
      );
      const parsedProviderOdd = Number(providerPrice.odd);

      providerOdd.status = normalizedProviderStatus;
      providerOdd.change = providerPrice.change ? providerPrice.change : "";

      if (normalizedProviderStatus == "deleted") {
        providerOdd.odd = null;
        providerOdd.impliedOdd = null;
        providerOdd.change = "";
      } else {
        providerOdd.odd = Number.isNaN(parsedProviderOdd) ? providerPrice.odd : parsedProviderOdd;
        providerOdd.impliedOdd = providerPrice.impliedOdd;
      }
    } else {
      providerOdd.odd = null;
      providerOdd.impliedOdd = null;
      providerOdd.status = "new";
      providerOdd.change = "";
    }

    return providerOdd;
  }
  function getProviderOddsNumbers(odd, tmpProviders = []) {
    const oddPrices = getNormalizedOddPrices(odd);
    const pricesByProvider = new Map(
      oddPrices.map((providerPrice) => {
        return [`${providerPrice.providerId}`, providerPrice];
      })
    );

    const providersToRender =
      tmpProviders && tmpProviders.length
        ? tmpProviders
        : oddPrices.map((providerPrice) => {
            return providerPrice.providerId;
          });

    const providerOddsNumbers = [];

    for (let index = 0; index < providersToRender.length; index++) {
      const provider = providersToRender[index];
      const providerValue = getProviderValue(provider);
      const providerPrice = pricesByProvider.get(providerValue);
      const providerOdd = createProviderOdd(odd, providerPrice);
      const normalizedProviderStatus = getNormalizedPriceStatus(providerOdd.status, "new");

      if (normalizedProviderStatus == "deleted" || normalizedProviderStatus == "suspended") {
        continue;
      }

      const rawProviderOdd = providerOdd.odd;
      const isEmptyProviderOdd =
        rawProviderOdd === undefined ||
        rawProviderOdd === null ||
        (typeof rawProviderOdd === "string" && rawProviderOdd.trim() === "");

      if (isEmptyProviderOdd) {
        continue;
      }

      const parsedProviderOdd = Number(rawProviderOdd);
      if (!Number.isNaN(parsedProviderOdd)) {
        providerOddsNumbers.push(parsedProviderOdd);
      }
    }

    return providerOddsNumbers;
  }

  function buildMarketMetricOdd(odd, metricValue) {
    const metricOdd = { ...odd };

    metricOdd.status = odd && odd.status ? odd.status : "new";
    metricOdd.change = "";
    metricOdd.impliedOdd = null;
    metricOdd.odd =
      metricValue === null || metricValue === undefined || Number.isNaN(metricValue)
        ? null
        : Math.round(metricValue * 10000) / 10000;

    return metricOdd;
  }

  function getMarketMetricOdds(odd, tmpProviders = []) {
    const providerOddsNumbers = getProviderOddsNumbers(odd, tmpProviders);

    if (!providerOddsNumbers.length) {
      return {
        averageOdd: buildMarketMetricOdd(odd, null),
        topOdd: buildMarketMetricOdd(odd, null),
      };
    }

    const averageValue =
      providerOddsNumbers.reduce((sum, providerOdd) => {
        return sum + providerOdd;
      }, 0) / providerOddsNumbers.length;
    const topValue = Math.max(...providerOddsNumbers);

    return {
      averageOdd: buildMarketMetricOdd(odd, averageValue),
      topOdd: buildMarketMetricOdd(odd, topValue),
    };
  }

  function createProvidersHeadersHolder(tmpProviders) {
    const providersColumns = [];

    for (let index = 0; index < tmpProviders.length; index++) {
      const provider = tmpProviders[index];
      const pv = getProviderMeta(provider);
      const providerLabel = pv ? pv.label : provider;

      providersColumns.push(
        <Grid
          key={provider + "header-" + index}
          className={`grid-item provider-${pv ? pv.info : ""}`}
          style={{
            padding: "0px 3px",
            marginTop: "1px",
            marginBottom: "1px",
            borderRadius: "4px",
          }}
          sx={{
            minWidth: "50px",
            maxWidth: "50px",
            fontSize: "0.8rem",
            lineHeight: "1.0",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {providerLabel}
        </Grid>
      );
    }

    return providersColumns;
  }

  function createMarketOddHeaderCell(key = "odd-header") {
    return (
      <Grid
        key={key}
        className={"grid-item"}
        style={{
          padding: "0px 3px",
          marginTop: "1px",
          marginBottom: "1px",
          borderRadius: "4px",
        }}
        sx={{
          minWidth: "50px",
          maxWidth: "50px",
          fontSize: "0.8rem",
          lineHeight: "1.0",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontWeight: "600",
        }}
      >
        Odd
      </Grid>
    );
  }
  function createMarketMetricHeaderCell(label, key) {
    return (
      <Grid
        key={key}
        className={"grid-item"}
        style={{
          padding: "0px 3px",
          marginTop: "1px",
          marginBottom: "1px",
          borderRadius: "4px",
        }}
        sx={{
          minWidth: "50px",
          maxWidth: "50px",
          fontSize: "0.8rem",
          lineHeight: "1.0",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontWeight: "600",
        }}
      >
        {label}
      </Grid>
    );
  }

  function createMarketOddRowHolder(activeOdds, chunkIndex = 0) {
    const horizontalProviderLabelWidth = HORIZONTAL_PROVIDER_LABEL_WIDTH + "px";

    return (
      <Grid
        disabled={true}
        container
        key={"market-odd-row-" + chunkIndex}
        className="grid-item"
        style={{
          padding: "0px 3px",
          marginTop: "1px",
          marginBottom: "1px",
          borderRadius: "4px",
        }}
      >
        <SoftBox sx={{ display: "flex !important", width: "100% !important" }}>
          <SoftBox
            sx={{
              minWidth: horizontalProviderLabelWidth,
              width: horizontalProviderLabelWidth,
              paddingTop: "5px !important",
              paddingBottom: "5px !important",
            }}
          >
            <div
              className="odd-label odd-trader"
              style={{
                fontSize: "0.7rem",
                lineHeight: "1.0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: "12px",
                }}
              >
                Odd
              </div>
            </div>
          </SoftBox>
          <SoftBox
            sx={{
              display: "flex !important",
              flexWrap: "nowrap",
              flex: "1 1 0",
              minWidth: 0,
            }}
          >
            {activeOdds.map((odd) => {
              return createOddValueCell(
                odd,
                "market-odd-row-" + odd.properties.uuid + "-" + chunkIndex,
                true,
                odd.properties.uuid,
                true,
                "default",
                isCurrentMarketFiltered()
              );
            })}
          </SoftBox>
        </SoftBox>
      </Grid>
    );
  }
  function createMarketMetricRowHolder(
    activeOdds,
    tmpProviders,
    chunkIndex = 0,
    rowLabel,
    metricType
  ) {
    const horizontalProviderLabelWidth = HORIZONTAL_PROVIDER_LABEL_WIDTH + "px";

    return (
      <Grid
        disabled={true}
        container
        key={"market-" + metricType + "-row-" + chunkIndex}
        className="grid-item"
        style={{
          padding: "0px 3px",
          marginTop: "1px",
          marginBottom: "1px",
          borderRadius: "4px",
        }}
      >
        <SoftBox sx={{ display: "flex !important", width: "100% !important" }}>
          <SoftBox
            sx={{
              minWidth: horizontalProviderLabelWidth,
              width: horizontalProviderLabelWidth,
              paddingTop: "5px !important",
              paddingBottom: "5px !important",
            }}
          >
            <div
              className="odd-label odd-trader"
              style={{
                fontSize: "0.7rem",
                lineHeight: "1.0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: "12px",
                }}
              >
                {rowLabel}
              </div>
            </div>
          </SoftBox>
          <SoftBox
            sx={{
              display: "flex !important",
              flexWrap: "nowrap",
              flex: "1 1 0",
              minWidth: 0,
            }}
          >
            {activeOdds.map((odd) => {
              const marketMetricOdds = getMarketMetricOdds(odd, tmpProviders);
              const metricOdd =
                metricType == "top" ? marketMetricOdds.topOdd : marketMetricOdds.averageOdd;

              return createOddValueCell(
                metricOdd,
                "market-" + metricType + "-row-" + odd.properties.uuid + "-" + chunkIndex,
                true,
                odd.properties.uuid,
                true,
                metricType
              );
            })}
          </SoftBox>
        </SoftBox>
      </Grid>
    );
  }

  function getOddClassName(odd, oddUuid = null) {
    const targetOddUuid = odd && odd.properties ? odd.properties.uuid : oddUuid;
    const isOperatorBlockedOdd = targetOddUuid
      ? operatorBlockedOdds.findIndex((bo) => bo.uuid == targetOddUuid) > -1
      : false;

    return isOperatorBlockedOdd
      ? odd.ml
        ? "filtered-grid-item-ml"
        : "filtered-grid-item"
      : odd.ml
      ? "grid-item-ml"
      : "grid-item";
  }

  function getOddDisplayName(odd, marketUniName) {
    return odd.name
      ? odd.name === marketUniName
        ? odd.properties.h + "::" + odd.properties.o + "::" + odd.properties.e
        : odd.name
      : odd.properties.h + "::" + odd.properties.o + "::" + odd.properties.e;
  }

  function normalizeOddDirectionToken(token) {
    if (token === undefined || token === null) return null;

    const normalizedToken = `${token}`.toLowerCase().replace(/[^a-z]/g, "");

    if (["over", "o"].includes(normalizedToken)) return "over";
    if (["under", "u"].includes(normalizedToken)) return "under";

    return null;
  }

  function getDirectionFromText(text) {
    if (text === undefined || text === null) return null;

    const tokens = `${text}`.toLowerCase().split(/[^a-z]+/).filter(Boolean);

    for (let index = 0; index < tokens.length; index++) {
      const direction = normalizeOddDirectionToken(tokens[index]);
      if (direction) return direction;
    }

    return normalizeOddDirectionToken(text);
  }

  function parseNumericLineValue(value) {
    if (typeof value == "number" && Number.isFinite(value)) {
      if (Math.abs(value) <= 1000) return value;
      return null;
    }

    if (typeof value != "string") return null;

    const normalizedValue = value.replace(/,/g, ".");
    const match = normalizedValue.match(/[-+]?\d+(?:\.\d+)?/);
    if (!match) return null;

    const parsedValue = Number(match[0]);
    if (Number.isNaN(parsedValue) || Math.abs(parsedValue) > 1000) return null;

    return parsedValue;
  }

  function getOddDirectionAndLine(odd, marketUniName) {
    if (!odd) return null;

    const oddLabel = getOddDisplayName(odd, marketUniName);
    const normalizedOddLabel = `${oddLabel}`.replace(/,/g, ".");

    const directionLineMatchers = [
      /(?:^|)(over|under|o|u)\s*([-+]?\d+(?:\.\d+)?)(?:|$)/i,
      /(?:^|)([-+]?\d+(?:\.\d+)?)\s*(over|under|o|u)(?:|$)/i,
    ];

    for (let index = 0; index < directionLineMatchers.length; index++) {
      const matcher = directionLineMatchers[index];
      const match = normalizedOddLabel.match(matcher);
      if (!match) continue;

      const direction = normalizeOddDirectionToken(match[1]) || normalizeOddDirectionToken(match[2]);
      const line = parseNumericLineValue(match[1]) ?? parseNumericLineValue(match[2]);

      if (direction && line !== null) {
        return {
          direction,
          line,
          lineKey: line.toFixed(4),
        };
      }
    }

    const valueCandidates = [
      odd.name,
      odd && odd.properties ? odd.properties.h : null,
      odd && odd.properties ? odd.properties.o : null,
      odd && odd.properties ? odd.properties.e : null,
      oddLabel,
    ];

    let direction = null;
    let line = null;

    for (let index = 0; index < valueCandidates.length; index++) {
      if (!direction) {
        direction = getDirectionFromText(valueCandidates[index]);
      }

      if (line === null) {
        line = parseNumericLineValue(valueCandidates[index]);
      }
    }

    if (!direction || line === null) return null;

    return {
      direction,
      line,
      lineKey: line.toFixed(4),
    };
  }

  function getVerticalAlternatedTotalsOdds(activeOdds, marketUniName) {
    if (!activeOdds || activeOdds.length < 2) return activeOdds;

    const matchedOdds = [];
    const unmatchedOdds = [];
    let overCount = 0;
    let underCount = 0;

    for (let index = 0; index < activeOdds.length; index++) {
      const odd = activeOdds[index];
      const oddMeta = getOddDirectionAndLine(odd, marketUniName);

      if (!oddMeta) {
        unmatchedOdds.push({ odd, index });
        continue;
      }

      matchedOdds.push({
        odd,
        index,
        direction: oddMeta.direction,
        line: oddMeta.line,
        lineKey: oddMeta.lineKey,
      });

      if (oddMeta.direction == "over") overCount++;
      if (oddMeta.direction == "under") underCount++;
    }

    const minimumMatchedOdds = Math.max(2, Math.floor(activeOdds.length * 0.7));
    const isTotalsLikeMarket = matchedOdds.length >= minimumMatchedOdds;

    if (!isTotalsLikeMarket || !overCount || !underCount) return activeOdds;

    const groupedByLine = new Map();

    for (let index = 0; index < matchedOdds.length; index++) {
      const matchedOdd = matchedOdds[index];

      if (!groupedByLine.has(matchedOdd.lineKey)) {
        groupedByLine.set(matchedOdd.lineKey, {
          line: matchedOdd.line,
          over: [],
          under: [],
        });
      }

      const lineGroup = groupedByLine.get(matchedOdd.lineKey);
      if (matchedOdd.direction == "over") {
        lineGroup.over.push(matchedOdd);
      } else {
        lineGroup.under.push(matchedOdd);
      }
    }

    const orderedMatchedOdds = [];
    const sortedLineGroups = Array.from(groupedByLine.values()).sort((a, b) => a.line - b.line);

    for (let groupIndex = 0; groupIndex < sortedLineGroups.length; groupIndex++) {
      const lineGroup = sortedLineGroups[groupIndex];
      const overOdds = lineGroup.over.sort((a, b) => a.index - b.index);
      const underOdds = lineGroup.under.sort((a, b) => a.index - b.index);
      const maxLength = Math.max(overOdds.length, underOdds.length);

      for (let index = 0; index < maxLength; index++) {
        if (overOdds[index]) orderedMatchedOdds.push(overOdds[index]);
        if (underOdds[index]) orderedMatchedOdds.push(underOdds[index]);
      }
    }

    if (orderedMatchedOdds.length != matchedOdds.length) return activeOdds;

    const reorderedOdds = orderedMatchedOdds.map((matchedOdd) => {
      return matchedOdd.odd;
    });

    if (!unmatchedOdds.length) return reorderedOdds;

    const orderedUnmatchedOdds = unmatchedOdds
      .sort((a, b) => a.index - b.index)
      .map((unmatchedOdd) => {
        return unmatchedOdd.odd;
      });

    return reorderedOdds.concat(orderedUnmatchedOdds);
  }

  function createOddValueCell(
    odd,
    key,
    matchHeaderWidth = false,
    oddUuid = null,
    forceBaseOddValue = false,
    cellHighlightType = "default",
    forceDashValue = false
  ) {
    const minColumnWidth = MIN_ODD_COLUMN_WIDTH + "px";
    const columnWidth = matchHeaderWidth ? "auto" : minColumnWidth;
    const valueMaxWidth = matchHeaderWidth ? "100%" : minColumnWidth;
    const columnFlex = matchHeaderWidth ? "1 1 " + minColumnWidth : "0 0 " + minColumnWidth;
    const oddStatus =
      odd && odd.status ? `${odd.status}`.toLowerCase().replace(/[^a-z]/g, "") : "new";
    const rawOddValue = odd ? odd.odd : null;
    const isEmptyOddValue =
      rawOddValue === undefined ||
      rawOddValue === null ||
      (typeof rawOddValue === "string" && rawOddValue.trim() === "");
    const showDeletedOdd = !forceDashValue && oddStatus == "deleted";
    const showLockIcon = !forceDashValue && oddStatus == "suspended";
    const highlightEmptyOdd = isEmptyOddValue;
    const oddDisplayValue =
      forceDashValue || highlightEmptyOdd ? "-" : showOddWithStyle(odd, forceBaseOddValue);
    const metricBackgroundColor =
      cellHighlightType == "average"
        ? "rgba(255, 165, 0, 0.10)"
        : cellHighlightType == "top"
        ? "rgba(255, 255, 0, 0.10)"
        : "";
    const cellBackgroundColor = highlightEmptyOdd ? "rgba(255, 0, 0, 0.10)" : metricBackgroundColor;

    return (
      <Grid
        disabled={true}
        container
        key={key}
        className={getOddClassName(odd, oddUuid)}
        style={{
          padding: "0px 3px",
          marginTop: "1px",
          marginBottom: "1px",
          borderRadius: "4px",
          backgroundColor: cellBackgroundColor,
        }}
        sx={{
          boxSizing: "border-box",
          minWidth: minColumnWidth,
          maxWidth: matchHeaderWidth ? "none" : minColumnWidth,
          width: columnWidth,
          flex: columnFlex,
        }}
      >
        <Grid
          container
          style={{
            position: "relative",
            width: "100%",
            textAlign: "right",
            marginLeft: "auto",
          }}
          sx={{
            display: showLockIcon || showDeletedOdd ? "none" : "",
            width: "100% !important",
          }}
        >
          <Grid item sx={{ maxWidth: valueMaxWidth, width: "100%" }}>
            <SoftTypography
              className={
                odd.change ? "odd-value-trader value-" + odd.change : "odd-value odd-value-trader"
              }
              sx={{ fontSize: "0.7rem !important", lineHeight: "1.0" }}
            >
              {oddDisplayValue}
            </SoftTypography>
          </Grid>
        </Grid>
        <Grid
          item
          style={{
            position: "relative",
            textAlign: "right",
            marginLeft: "auto",
            fontSize: "0.7rem",
          }}
          sx={{ display: showLockIcon && !showDeletedOdd ? "" : "none" }}
        >
          <Icon sx={{ fontSize: "1rem" }}>lock_outlined_icon </Icon>
        </Grid>
      </Grid>
    );
  }

  function createProvidersHolder(odd, tmpProviders = []) {
    const providersColumns = [];
    const oddPrices = getNormalizedOddPrices(odd);
    const pricesByProvider = new Map(
      oddPrices.map((providerPrice) => {
        return [`${providerPrice.providerId}`, providerPrice];
      })
    );

    const providersToRender =
      tmpProviders && tmpProviders.length
        ? tmpProviders
        : oddPrices.map((providerPrice) => {
            return providerPrice.providerId;
          });

    if (showOddColumn) {
      providersColumns.push(
        createOddValueCell(
          odd,
          odd.properties.uuid + "-market-odd",
          false,
          odd.properties.uuid,
          true,
          "default",
          isCurrentMarketFiltered()
        )
      );
    }

    for (let index = 0; index < providersToRender.length; index++) {
      const provider = providersToRender[index];
      const providerValue = getProviderValue(provider);
      const providerPrice = pricesByProvider.get(providerValue);
      const providerOdd = createProviderOdd(odd, providerPrice);

      providersColumns.push(
        createOddValueCell(
          providerOdd,
          odd.properties.uuid + "-" + provider + "-" + index,
          false,
          odd.properties.uuid
        )
      );
    }

    if (showAverageColumn || showTopColumn) {
      const marketMetricOdds = getMarketMetricOdds(odd, tmpProviders);

      if (showAverageColumn) {
        providersColumns.push(
          createOddValueCell(
            marketMetricOdds.averageOdd,
            odd.properties.uuid + "-market-average",
            false,
            odd.properties.uuid,
            true,
            "average"
          )
        );
      }

      if (showTopColumn) {
        providersColumns.push(
          createOddValueCell(
            marketMetricOdds.topOdd,
            odd.properties.uuid + "-market-top",
            false,
            odd.properties.uuid,
            true,
            "top"
          )
        );
      }
    }

    return providersColumns;
  }

  function createOddsHeadersHolder(odds, marketUniName) {
    const oddsColumns = [];

    for (let index = 0; index < odds.length; index++) {
      const odd = odds[index];
      const oddLabel = getOddDisplayName(odd, marketUniName);

      oddsColumns.push(
        <Grid
          key={odd.properties.uuid + "header"}
          className={"grid-item"}
          title={oddLabel}
          style={{
            padding: "0px 3px",
            marginTop: "1px",
            marginBottom: "1px",
            borderRadius: "4px",
          }}
          sx={{
            boxSizing: "border-box",
            minWidth: MIN_ODD_COLUMN_WIDTH + "px",
            width: "auto",
            flex: "1 1 " + MIN_ODD_COLUMN_WIDTH + "px",
            fontSize: "0.8rem",
            lineHeight: "1.1",
            overflow: "visible",
            textOverflow: "clip",
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {oddLabel}
        </Grid>
      );
    }

    return oddsColumns;
  }

  function createProvidersRowsHolder(activeOdds, tmpProviders, chunkIndex = 0) {
    const providerRows = [];
    const horizontalProviderLabelWidth = HORIZONTAL_PROVIDER_LABEL_WIDTH + "px";

    for (let index = 0; index < tmpProviders.length; index++) {
      const provider = tmpProviders[index];
      const pv = getProviderMeta(provider);
      const providerLabel = pv ? pv.label : provider;

      providerRows.push(
        <Grid
          disabled={true}
          container
          key={provider + "-row-" + chunkIndex + "-" + index}
          className="grid-item"
          style={{
            padding: "0px 3px",
            marginTop: "1px",
            marginBottom: "1px",
            borderRadius: "4px",
          }}
        >
          <SoftBox sx={{ display: "flex !important", width: "100% !important" }}>
            <SoftBox
              sx={{
                minWidth: horizontalProviderLabelWidth,
                width: horizontalProviderLabelWidth,
                paddingTop: "5px !important",
                paddingBottom: "5px !important",
              }}
            >
              <div
                className="odd-label odd-trader"
                style={{
                  fontSize: "0.7rem",
                  lineHeight: "1.0",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: "12px",
                  }}
                  className={`provider-${pv ? pv.info : ""}`}
                >
                  {providerLabel}
                </div>
              </div>
            </SoftBox>
            <SoftBox
              sx={{
                display: "flex !important",
                flexWrap: "nowrap",
                flex: "1 1 0",
                minWidth: 0,
              }}
            >
              {activeOdds.map((odd) => {
                const providerValue = getProviderValue(provider);
                const providerPrice = getNormalizedOddPrices(odd).find((pOdd) => {
                  return `${pOdd.providerId}` == providerValue;
                });
                const providerOdd = createProviderOdd(odd, providerPrice);

                return createOddValueCell(
                  providerOdd,
                  provider + "-" + odd.properties.uuid + "-" + chunkIndex + "-" + index,
                  true,
                  odd.properties.uuid
                );
              })}
            </SoftBox>
          </SoftBox>
        </Grid>
      );
    }

    return providerRows;
  }

  function getHorizontalOddsPerLine(activeOddsCount) {
    if (!activeOddsCount) return 1;

    if (!oddsGridWidth) {
      const fallbackOddsPerLine = HORIZONTAL_ODDS_PER_LINE_FALLBACK[marketsPerRow] || 8;
      return Math.max(1, Math.min(activeOddsCount, fallbackOddsPerLine));
    }

    const horizontalAvailableWidth = Math.max(
      MIN_ODD_COLUMN_WIDTH,
      oddsGridWidth - HORIZONTAL_PROVIDER_LABEL_WIDTH - HORIZONTAL_LAYOUT_SAFE_PADDING
    );

    return Math.max(
      1,
      Math.min(activeOddsCount, Math.floor(horizontalAvailableWidth / MIN_ODD_COLUMN_WIDTH))
    );
  }

  function createColumn(odds, index, marketUniName, tmpProviders) {
    let rows = [];
    let activeOdds = odds.filter((item) => !isDeletedStatus(item.status));

    if (oddsProvidersLayout != "horizontal") {
      activeOdds = getVerticalAlternatedTotalsOdds(activeOdds, marketUniName);
    }

    if (oddsProvidersLayout == "horizontal") {
      const horizontalProviderLabelWidth = HORIZONTAL_PROVIDER_LABEL_WIDTH + "px";
      const horizontalOddsPerLine = getHorizontalOddsPerLine(activeOdds.length);
      const oddsChunks = [];

      for (
        let chunkStart = 0;
        chunkStart < activeOdds.length;
        chunkStart += horizontalOddsPerLine
      ) {
        oddsChunks.push(activeOdds.slice(chunkStart, chunkStart + horizontalOddsPerLine));
      }

      for (let chunkIndex = 0; chunkIndex < oddsChunks.length; chunkIndex++) {
        const oddsChunk = oddsChunks[chunkIndex];

        rows.push(
          <Grid
            id={"market-providers-holder-" + chunkIndex}
            key={"market-providers-holder-" + chunkIndex}
            disabled={true}
            container
            sx={{
              paddingRight: "4px !important",
              position: "sticky",
              top: "28px",
              zIndex: 14,
              backgroundColor: "#dfe6ec",
            }}
          >
            <SoftBox sx={{ display: "flex !important", width: "100% !important" }}>
              <SoftBox
                sx={{
                  minWidth: horizontalProviderLabelWidth,
                  width: horizontalProviderLabelWidth,
                  padding: "2px !important",
                }}
              >
                <SoftTypography sx={{ fontSize: "0.7rem", fontWeight: "600" }}></SoftTypography>
              </SoftBox>
              <SoftBox
                sx={{
                  display: "flex !important",
                  flex: "1 1 0",
                  minWidth: 0,
                  flexWrap: "nowrap",
                }}
              >
                {createOddsHeadersHolder(oddsChunk, marketUniName)}
              </SoftBox>
            </SoftBox>
          </Grid>
        );

        if (showOddColumn) {
          rows.push(createMarketOddRowHolder(oddsChunk, chunkIndex));
        }
        rows = rows.concat(createProvidersRowsHolder(oddsChunk, tmpProviders, chunkIndex));
        if (showAverageColumn) {
          rows.push(
            createMarketMetricRowHolder(oddsChunk, tmpProviders, chunkIndex, "Average", "average")
          );
        }
        if (showTopColumn) {
          rows.push(
            createMarketMetricRowHolder(oddsChunk, tmpProviders, chunkIndex, "Highest", "top")
          );
        }
      }

      return (
        <Grid
          container
          key={"column-" + index}
          className="grid-container"
          sx={{ margin: "2px !important", rowGap: "1px" }}
        >
          {rows}
        </Grid>
      );
    }

    rows.push(
      <Grid
        id="market-providers-holder"
        key="market-providers-holder"
        disabled={true}
        container
        sx={{
          paddingRight: "4px !important",
          position: "sticky",
          top: "28px",
          zIndex: 14,
          backgroundColor: "#dfe6ec",
          overflow: "hidden",
        }}
      >
        <SoftBox sx={{ display: "flex !important", width: "100% !important", minWidth: 0 }}>
          <SoftBox sx={{ flex: "1 1 0", minWidth: 0, padding: "2px !important" }}></SoftBox>
          <Grid
            container
            className="trader-providers-container"
            sx={{
              width: "auto !important",
              flex: "0 0 auto",
              minWidth: "fit-content",
              maxWidth: "100%",
            }}
          >
            <SoftBox
              sx={{ display: "flex !important", flexWrap: "nowrap", minWidth: "fit-content" }}
            >
              {showOddColumn ? createMarketOddHeaderCell("market-odd-header") : ""}
              {createProvidersHeadersHolder(tmpProviders)}
              {showAverageColumn
                ? createMarketMetricHeaderCell("Average", "market-average-header")
                : ""}
              {showTopColumn ? createMarketMetricHeaderCell("Highest", "market-top-header") : ""}
            </SoftBox>
          </Grid>
        </SoftBox>
      </Grid>
    );

    for (let index = 0; index < activeOdds.length; index++) {
      const odd = activeOdds[index];
      rows.push(
        <Grid
          disabled={true}
          container
          key={odd.properties.uuid}
          className={getOddClassName(odd)}
          style={{
            padding: "0px 3px",
            marginTop: "1px",
            marginBottom: "1px",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <SoftBox sx={{ display: "flex !important", width: "100% !important", minWidth: 0 }}>
            <SoftBox sx={{ flex: "1 1 0", minWidth: 0, paddingRight: "4px" }}>
              <div
                className="odd-label odd-trader"
                style={{
                  fontSize: "0.7rem",
                  lineHeight: "1.2",
                  whiteSpace: "normal",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  wordBreak: "break-word",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  paddingTop: "5px !important",
                  paddingBottom: "5px !important",
                }}
              >
                <div id={odd.properties.uuid} style={{ display: "block", minHeight: "12px" }}>
                  {getOddDisplayName(odd, marketUniName)}
                </div>
              </div>
            </SoftBox>
            <Grid
              container
              className="trader-providers-container"
              sx={{
                width: "auto !important",
                flex: "0 0 auto",
                minWidth: "fit-content",
                maxWidth: "100%",
              }}
            >
              <SoftBox
                sx={{ display: "flex !important", flexWrap: "nowrap", minWidth: "fit-content" }}
              >
                {createProvidersHolder(odd, tmpProviders)}
              </SoftBox>
            </Grid>
          </SoftBox>
        </Grid>
      );
    }

    return (
      <Grid
        container
        key={"column-" + index}
        className="grid-container"
        sx={{ margin: "2px !important", rowGap: "1px" }}
      >
        {rows}
      </Grid>
    );
  }

  function CreateGrid(m) {
    const columns = 1;
    const rowsCount = m.properties.column ? Math.ceil(m.odds.length / columns) : 1;

    m.odds.sort(function (a, b) {
      var aCon1 = a.column;
      var bCon1 = b.column;
      var aCon2 = a.sort;
      var bCon2 = b.sort;
      return aCon1 < bCon1 ? -1 : aCon1 > bCon1 ? 1 : aCon2 < bCon2 ? -1 : aCon2 > bCon2 ? 1 : 0;
    });

    let columnsDom = [];
    //console.log("columns: ", columns, " - rows: ", rowsCount, " - odds: ", m.odds.length);

    if (m.odds.length === 0) return columnsDom;
    for (let index = 0; index < columns; index++) {
      columnsDom.push(
        createColumn(
          m.odds.filter((odd, oddIndex) => {
            return oddIndex % columns == index;
          }),
          index,
          m.properties.uni_name,
          m.providers
        )
      );
    }
    return columnsDom;
    // if (m.odds[0].column == null) {
    //   for (let index = 0; index < columns; index++) {
    //     columnsDom.push(
    //       createColumn(
    //         m.odds.filter((odd, oddIndex) => {
    //           return oddIndex % columns == index;
    //         }),
    //         index,
    //         m.properties.uni_name
    //       )
    //     );
    //   }

    //   return columnsDom;
    // } else {
    //   for (let index = 0; index < columns; index++) {
    //     columnsDom.push(
    //       createColumn(
    //         m.odds.filter((odd) => odd.column == index),
    //         index,
    //         m.properties.uni_name
    //       )
    //     );
    //   }

    //   return columnsDom;
    // }
  }

  async function placeBet(e, oddUuid) {
    const userData = getUserData();
    let getOddsEndpoint = `/api/v1/bets/create`;
    let platformId = userData.defaultPlatform.value;
    //console.log(userData, oddUuid);
    let response = await postData(
      getOddsEndpoint,
      { currency_id: 1, amount: 1, odds: [oddUuid], platformId: platformId },
      {
        Authorization: getAuthorizationHeader(),
      }
    );
    if (response != null) {
      setApiresponse("success");
      setApiResponseMsg("Bet has been sent!");
      setNotification(true);
    } else {
      setApiresponse("error");
      setApiResponseMsg("Bet not Accepted. Something goes wrong try again please!");
      setNotification(true);
    }
  }

  async function setMarketFilter(e, marketId) {
    e.preventDefault();
    market.filtered = !market.filtered;
    const operBlockedMarkets = [...operatorBlockedMarkets];
    const marketIndex = operBlockedMarkets.findIndex((bm) => bm.uuid == marketId);
    if (marketIndex == -1) {
      operBlockedMarkets.push({
        uuid: marketId,
        status: market.filtered,
      });
    } else {
      operBlockedMarkets.splice(marketIndex, 1);
    }
    setOperatorBlockedMarkets(operBlockedMarkets);
    setMarketData(market);

    let fullBlockeddOdds = market.odds
      .filter((o) => o.filtered)
      .map((o) => {
        return o.properties.uuid;
      })
      .concat(
        market.delblockedOdds.map((o) => {
          return o.uuid;
        })
      );

    fullBlockeddOdds = [...new Set(fullBlockeddOdds)];
    console.log(fullBlockeddOdds);

    informParent(e, {
      type: "marketChange",
      data: {
        uuid: marketId,
        status: market.filtered,
        odds: fullBlockeddOdds,
      },
    });
    e.stopPropagation();
  }

  async function setOddFilter(e, odd) {
    e.preventDefault();
    const f_odd = market.odds.find((fo) => fo.properties.uuid == odd.properties.uuid);
    f_odd.filtered = !f_odd.filtered;
    const operBlockedOdds = [...operatorBlockedOdds];
    const oddIndex = operBlockedOdds.findIndex((bo) => bo.uuid == odd.properties.uuid);
    if (oddIndex == -1) {
      operBlockedOdds.push({
        uuid: odd.properties.uuid,
        status: f_odd.filtered,
        market_uuid: market.marketId,
      });
    } else {
      operBlockedOdds.splice(oddIndex, 1);
    }
    setOperatorBlockedOdds(operBlockedOdds);
    setMarketData(market);

    let fullBlockeddOdds = market.odds
      .filter((o) => o.filtered)
      .map((o) => {
        return o.properties.uuid;
      })
      .concat(
        market.delblockedOdds.map((o) => {
          return o.uuid;
        })
      );

    fullBlockeddOdds = [...new Set(fullBlockeddOdds)];
    console.log(fullBlockeddOdds);

    informParent(e, {
      type: "marketChange",
      data: {
        uuid: market.marketId,
        status: marketData.filtered,
        odds: fullBlockeddOdds,
      },
    });
    e.stopPropagation();
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setNotification(false);
  };

  useEffect(() => {
    if (market) {
      // console.log(oddsStyle);
      for (let index = 0; index < market.odds.length; index++) {
        const odd = market.odds[index];
        const f_odd = cachedOdds.find((fo) => fo.properties.uuid == odd.properties.uuid);
        for (let i = 0; i < odd.prices.length; i++) {
          const p_odd = odd.prices[i];
          const f_podd = f_odd
            ? f_odd.prices.find((fo) => fo.providerId == p_odd.providerId)
            : null;
          p_odd.change = null;
          if (f_podd) {
            if (f_podd.odd !== p_odd.odd) {
              // console.log(market.name);
              // console.log("f_odd: ", odd.name, "->", f_odd.value, " - ", odd.value);
              if (p_odd.status.toLowerCase() === "new") {
                if (p_odd.odd - f_podd.odd > 0) p_odd.change = "up";
                if (p_odd.odd - f_podd.odd < 0) p_odd.change = "down";
                // console.log("change -> ", odd.change);
              } else {
                //console.log("changes with STATUS: ", odd.status.toLowerCase());
                if (p_odd.odd - f_podd.odd > 0) p_odd.change = "up";
                if (p_odd.odd - f_podd.odd < 0) p_odd.change = "down";
              }
            }
          }
        }
        odd.change = null;
        if (f_odd) {
          if (f_odd.odd !== odd.odd) {
            // console.log(market.name);
            // console.log("f_odd: ", odd.name, "->", f_odd.value, " - ", odd.value);
            if (odd.status.toLowerCase() === "new") {
              if (odd.odd - f_odd.odd > 0) odd.change = "up";
              if (odd.odd - f_odd.odd < 0) odd.change = "down";
              // console.log("change -> ", odd.change);
            } else {
              //console.log("changes with STATUS: ", odd.status.toLowerCase());
              if (odd.odd - f_odd.odd > 0) odd.change = "up";
              if (odd.odd - f_odd.odd < 0) odd.change = "down";
            }
          }
        }
      }

      setCachedOdds(market.odds);

      setMarketData(market);
    }
  }, [market]);

  useEffect(() => {
    if (massClose !== null) {
      //console.log("forceCloseHandling..", massClose);
      setOpen(!massClose);    }
  }, [massClose]);

  useEffect(() => {
    if (releaseOperBlocked) {
      setOperatorBlockedMarkets([]);
      setOperatorBlockedOdds([]);
      informParent(new Event("fake event"), { type: "releaseCompleted", data: null });
    }
  }, [releaseOperBlocked]);

  useEffect(() => {
    if (!oddsGridHolderRef.current || oddsProvidersLayout != "horizontal") return;

    const updateOddsGridWidth = () => {
      if (!oddsGridHolderRef.current) return;

      const nextWidth = Math.round(oddsGridHolderRef.current.getBoundingClientRect().width);

      setOddsGridWidth((prevWidth) => {
        return prevWidth == nextWidth ? prevWidth : nextWidth;
      });
    };

    updateOddsGridWidth();

    if (typeof ResizeObserver == "undefined") {
      window.addEventListener("resize", updateOddsGridWidth);
      return () => {
        window.removeEventListener("resize", updateOddsGridWidth);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      updateOddsGridWidth();
    });

    resizeObserver.observe(oddsGridHolderRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    marketData,
    oddsProvidersLayout,
    open,
    marketsPerRow,
    showOddColumn,
    showAverageColumn,
    showTopColumn,
  ]);

  return (
    <React.Fragment>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"topright"}
        open={notification}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={`${apiResponse}`}
          sx={{ width: "100%" }}
          className={`api-${apiResponse}-notifications`}
        >
          {apiResponseMsg}
        </Alert>
      </Snackbar>
      <Table
        className="market-box"
        sx={{
          margin: "1px !important",
          borderRadius: "5px !important",
          overflow: "visible !important",
        }}
      >
        {marketData ? (
          <TableBody>
            <TableRow
              id={marketData.marketId}
              className={
                operatorBlockedMarkets.findIndex((bm) => bm.uuid == marketData.marketId) > -1
                  ? open
                    ? "market-filtered-open"
                    : "market-filtered-closed"
                  : open
                  ? "market-open"
                  : "market-closed"
              }
              sx={{
                "& > *": { borderBottom: "unset" },
                "& td": { paddingTop: "1px", paddingBottom: "1px" },
                position: "sticky",
                top: 0,
                zIndex: 15,
              }}
              onClick={(e) => clickHandler(e, { name: marketData.name, id: marketData.marketId })}
              title={marketData.name + "-" + marketData.id}
            >
              <TableCell
                style={{
                  borderBottom: "unset",
                  width: "100%",
                  padding: "1px 3px",
                  backgroundColor: "inherit",
                }}
                align="left"
              >
                <span
                  style={{
                    display: marketData.name ? "inline-block" : "none",
                    marginRight: "4px",
                    fontSize: "0.875rem",
                    lineHeight: "1.1",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {marketData.name}
                </span>
                <span
                  style={{
                    display: marketData.name ? "none" : "inline-block",
                    marginRight: "4px",
                    fontSize: "0.72rem",
                    lineHeight: "1.1",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {marketData.properties.uni_name} ({marketData.properties.numbers.join("-")})
                </span>
                {/* <Tooltip
                  title={!marketData.filtered ? "Block Market" : "UnBlock Market"}
                  placement="top"
                >
                  <Switch
                    className="test"
                    name="market-filter-switch"
                    color="info"
                    checked={
                      operatorBlockedMarkets.findIndex((bm) => bm.uuid == marketData.marketId) > -1
                        ? !operatorBlockedMarkets.find((bm) => bm.uuid == marketData.marketId)
                            .status
                        : !marketData.filtered
                    }
                  />
                </Tooltip> */}
              </TableCell>

              <TableCell
                style={{
                  minWidth: "16px",
                  width: "16px",
                  borderBottom: "unset",
                  position: "relative",
                  padding: "1px 3px",
                  backgroundColor: "inherit",
                }}
              >
                <Icon
                  sx={{
                    display: marketData.status.toLowerCase() == "suspended" ? "" : "none",
                    position: "absolute",
                    right: "18px",
                    top: "1px",
                    fontSize: "0.7rem",
                  }}
                >
                  lock_outlined_icon{" "}
                </Icon>
                <IconButton aria-label="expand row" size="small" sx={{ padding: "0px" }}>
                  {open ? (
                    <KeyboardArrowUpIcon sx={{ fontSize: "0.85rem" }} />
                  ) : (
                    <KeyboardArrowDownIcon sx={{ fontSize: "0.85rem" }} />
                  )}
                </IconButton>
              </TableCell>
            </TableRow>
            <TableRow
              id={marketData.marketId + "-odds-Holder "}
              sx={{
                "& > *": { borderBottom: "unset" },
                "& td": { paddingTop: "1px", paddingBottom: "1px" },
              }}
              style={{ display: open ? "" : "none" }}
            >
              <TableCell
                style={{ borderBottom: "unset", width: "100%", padding: "0px 0px 1px" }}
                align="left"
                colSpan={2}
                className="odds-column-grid-holder"
              >
                <SoftBox
                  ref={oddsGridHolderRef}
                  className="odds-grid-holder"
                  sx={{ margin: "1px !important" }}
                >
                  {CreateGrid(marketData)}
                </SoftBox>
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody></TableBody>
        )}
      </Table>
    </React.Fragment>
  );
}

// Setting default values for the props of CreatePlatform
MarketView.defaultProps = {
  oddsStyle: "decimal",
  oddsProvidersLayout: "vertical",
  marketsPerRow: 1,
  showOddColumn: true,
  showAverageColumn: true,
  showTopColumn: true,
};

// Typechecking props for the DataTable
MarketView.propTypes = {
  market: PropTypes.object,
  massClose: PropTypes.bool,
  oddsStyle: PropTypes.string,
  informParent: PropTypes.func,
  releaseOperBlocked: PropTypes.bool,
  oddsProvidersLayout: PropTypes.string,
  marketsPerRow: PropTypes.number,
  showOddColumn: PropTypes.bool,
  showAverageColumn: PropTypes.bool,
  showTopColumn: PropTypes.bool,
};

export default MarketView;
